// ============================================================================
// WebSocket transport — send, connect, message dispatch, reconnection
// ============================================================================
// Extracted from main.js. Handles the WebSocket lifecycle (connect,
// heartbeat, reconnect) and dispatches all incoming ACP protocol messages.
//
// Depends on: DOM elements (dom.js), shell handlers (shells.js),
//   notifications (notifications.js), message rendering (message-rendering.js),
//   cache (cache.js), persistence (persistence.js), model picker
//   (model-picker.js), sessions modal (sessions-modal.js), tab title
//   (tabtitle.js), and many functions/state variables from main.js passed
//   via initWebSocket(deps).
//
// Globals from shared.js: Token, currentUser.

import {
  reconnectBanner, inner, messagesEl, input, sendBtn, attachBtn,
  sessionsOverlay, onboardStart,
} from "./dom.js";
import { onShellCreated, onShellOutput, onShellExit, onShellList } from "./shells.js";
import { bumpUnread, maybeNotify, notifTitle } from "./notifications.js";
import {
  handleUpdate, sysMsg, showReplayLoader, hideReplayLoader, updateReplayLoader,
  renderPermission, dismissPermissionCard, renderState,
  computePrependSuffixStart, flushPrependBuffers,
} from "./message-rendering.js";
import {
  renderElicitation, dismissElicitationCard, completeElicitationCard,
} from "./elicitation.js";
import { clearCachedSession } from "./cache.js";
import { saveActiveSession } from "./persistence.js";
import { setConfigOptions, renderModelPicker, restoreSavedModel } from "./model-picker.js";
import { updateSessionsBadge, renderSessionsModal } from "./sessions-modal.js";
import { tabAnnounce, updateTabTitle, setActiveCwd } from "./tabtitle.js";

// ---- deps (set by initWebSocket) ----
let deps = null;

// ---- internal reconnect state ----
let reconnectTimer = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_DELAY = 10000; // cap at 10s
let connected = false;
// Hoisted to module scope so probeOrReconnect() can clear a stale
// watchdog left over from a backgrounded ping cycle.
let pongTimeout = null;

export function initWebSocket(d) { deps = d; }

// Tool names whose completed output requires user attention (Sentry,
// Compass). Matched case-insensitively against the tool's `name`
// (programmatic) or `title` (human-readable, prefix match). Kept in
// sync with the server-side ATTENTION_TOOLS list in devin-session.ts.
const ATTENTION_TOOLS = ["sentry", "compass"];
function isAttentionTool(name, title) {
  const n = (name || "").toLowerCase().trim();
  if (n && ATTENTION_TOOLS.includes(n)) return true;
  const t = (title || "").toLowerCase().trim();
  if (t) {
    for (const tool of ATTENTION_TOOLS) {
      if (t.startsWith(tool + ":") || t.startsWith(tool + " ") || t === tool) return true;
    }
  }
  return false;
}

export function send(msg) {
  const ws = deps.getWs();
  if (ws && ws.readyState === WebSocket.OPEN) { ws.send(JSON.stringify(msg)); return true; }
  return false;
}
export function connect() {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const token = Token.get();
  if (!token) { location.href = "/login.html"; return; }
  // Detach the old socket's handlers before closing so its onclose
  // can't fire scheduleReconnect and race with this new connect —
  // UNLESS intentionalClose is set (newSession/changeFolder close the
  // socket explicitly and rely on onclose to consume the flag).
  const ws = deps.getWs();
  if (ws) {
    if (!deps.getIntentionalClose()) {
      ws.onclose = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
    }
    try { ws.close(); } catch {}
  }
  const newWs = new WebSocket(`${proto}//${location.host}/ws?token=${encodeURIComponent(token)}`);
  deps.setWs(newWs);
  connected = false;
  // Keepalive ping — prevents proxy/load-balancer from killing idle
  // connections. The server responds with "pong"; if no pong arrives
  // within HEARTBEAT_TIMEOUT_MS, the connection is treated as dead and
  // closed so onclose → scheduleReconnect fires (catches silently-dead
  // sockets where onclose never fires on its own).
  const HEARTBEAT_TIMEOUT_MS = 7000;
  // When the tab is hidden, Chrome throttles timers intensively (~1 fire/min
  // after 5 min). A 7s pong watchdog under that regime produces false
  // "connection stalled" detections and reconnect churn while minimized —
  // especially for an idle session (no streaming traffic to feed the
  // watchdog, which otherwise clears on ANY incoming message). Widen the
  // tolerance when hidden; on refocus the next ping cycle re-checks
  // liveness within 25s, so a genuinely dead socket is still caught fast.
  const HIDDEN_HEARTBEAT_TIMEOUT_MS = 120000;
  let pingInterval = null;
  let readyTimeout = null;
  newWs.onopen = () => {
    connected = true;
    reconnectAttempts = 0;
    hideReconnectBanner();
    const pendingStart = deps.getPendingStart();
    // A bare connect (fresh load with no saved session, or a refocus
    // reconnect) should still populate the sidebar immediately — the
    // gateway answers list_sessions from its own DB, no ACP needed.
    const expectsSession = !!pendingStart || deps.getResuming() || !!deps.getCurrentSessionId();
    if (pendingStart) {
      deps.setPendingStart(null);
      deps.doStartSession(pendingStart.cwd, pendingStart.sessionId);
    }
    if (!expectsSession) {
      send({ type: "list_sessions" });
    }
    pingInterval = setInterval(() => {
      const ws = deps.getWs();
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
        // Expect a pong within the (visibility-dependent) timeout;
        // otherwise the socket is silently dead — force-close to
        // trigger reconnect.
        if (pongTimeout) clearTimeout(pongTimeout);
        const timeoutMs = document.hidden ? HIDDEN_HEARTBEAT_TIMEOUT_MS : HEARTBEAT_TIMEOUT_MS;
        pongTimeout = setTimeout(() => {
          pongTimeout = null;
          console.warn("[ws] No pong within " + timeoutMs + "ms — closing dead socket");
          deps.setStatus("err", "connection stalled");
          try { ws.close(); } catch {}
        }, timeoutMs);
      }
    }, 25000);
    // If a session start is expected but no ready/session_loaded arrives
    // within 60s, show timeout and reconnect. Bare connects (onboarding)
    // skip this — there is no session to wait for, and killing the socket
    // every minute would churn the sidebar refresh.
    if (expectsSession) {
      readyTimeout = setTimeout(() => {
        if (!deps.getSessionActive()) {
          sysMsg("Session is taking too long to start. Reconnecting…", true);
          deps.setStatus("err", "timeout");
          const ws = deps.getWs();
          try { ws.close(); } catch {}
        }
      }, 60000);
    }
  };
  newWs.onmessage = (raw) => {
    if (readyTimeout) { clearTimeout(readyTimeout); readyTimeout = null; }
    // Any message means the socket is alive — clear the pong watchdog.
    if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; }
    onMessage(raw);
  };
  newWs.onclose = (ev) => {
    connected = false;
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; }
    if (readyTimeout) { clearTimeout(readyTimeout); readyTimeout = null; }
    // 4001 = server rejected the token — go to login, don't reconnect
    if (ev.code === 4001) {
      Token.clear();
      currentUser = null;
      deps.setResuming(false);
      location.href = "/login.html";
      return;
    }
    if (deps.getIntentionalClose()) {
      deps.setIntentionalClose(false);
      return; // don't reconnect — a new session/folder is being started
    }
    // Auto-reconnect: keep chat view visible, show banner
    if (deps.getCurrentSessionId() || deps.getSessionActive() || deps.getResuming()) {
      deps.setSessionActive(false);
      deps.setStatus("err", "disconnected");
      deps.setBusy(false);
      showReconnectBanner();
      scheduleReconnect();
    } else {
      // No active session — go back to onboarding
      deps.showOnboarding();
    }
  };
  newWs.onerror = () => deps.setStatus("err", "socket error");
}

export function hideReconnectBanner() {
  reconnectBanner.classList.add("hidden");
}
function showReconnectBanner() {
  reconnectBanner.classList.remove("hidden");
}
function scheduleReconnect() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  // Save current session for resume
  const currentSessionId = deps.getCurrentSessionId();
  const activeCwd = deps.getActiveCwd();
  if (currentSessionId && activeCwd) {
    deps.setPendingStart({ cwd: activeCwd, sessionId: currentSessionId });
  }
  const delay = Math.min(1000 * Math.pow(1.5, reconnectAttempts), MAX_RECONNECT_DELAY);
  reconnectAttempts++;
  deps.setStatus("err", `reconnecting in ${Math.round(delay / 1000)}s…`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    deps.setStatus("err", "reconnecting…");
    connect();
  }, delay);
}

// Called from the reconnect button handler in main.js to clear any
// pending timer and reset the backoff before a manual retry.
export function resetReconnectState() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  reconnectAttempts = 0;
}

// Called on visibilitychange → visible / pageshow (bfcache restore).
// Mobile browsers (especially Android Chrome/PWA) freeze timers and kill
// idle sockets while backgrounded. The scheduled reconnect timer may have
// been throttled to the point it never fired, or the socket may have died
// silently (onclose never fired). This probes the connection and reconnects
// immediately if it's stale, so the user lands on a live session instead
// of waiting for a backoff timer that was frozen for minutes.
// Shared reconnect path for probeOrReconnect(). Sets pendingStart so
// onopen → doStartSession runs (which sends `start` to reattach to the
// current session, and `list_sessions` after ready/reattached to
// repopulate the sidebar). Without this, a silently-dead socket that
// never fired onclose → scheduleReconnect would leave pendingStart unset,
// so the new WS opens but no `start` is sent and the sidebar stays empty.
//
// We null out the old socket's handlers instead of using the
// intentionalClose flag. The flag leaks when the old socket is already
// CLOSED (no onclose fires to consume it), which then suppresses
// reconnect for the NEW socket — causing the app to go dead silently.
// Nulling handlers is safe because connect() replaces deps.setWs()
// immediately, so the old socket is already orphaned.
function reconnectFromProbe() {
  resetReconnectState();
  const currentSessionId = deps.getCurrentSessionId();
  const activeCwd = deps.getActiveCwd();
  if (currentSessionId && activeCwd) {
    deps.setPendingStart({ cwd: activeCwd, sessionId: currentSessionId });
  }
  // connect() will null the old socket's handlers and close it, so
  // no onclose → scheduleReconnect race. We don't use intentionalClose
  // here because it leaks when the old socket is already CLOSED (no
  // onclose fires to consume the flag), which then suppresses reconnect
  // for the NEW socket — causing the app to go dead silently.
  connect();
}

export function probeOrReconnect() {
  const ws = deps.getWs();
  // Socket is already trying to connect — don't interrupt it. Killing a
  // CONNECTING socket triggers its onclose → scheduleReconnect, and if
  // visibilitychange fires again (Android fires it in bursts), each call
  // kills the in-flight connect and starts a new one → reconnect loop.
  if (ws && ws.readyState === WebSocket.CONNECTING) return;
  // A reconnect is already scheduled (onclose → scheduleReconnect fired
  // while hidden, timer was throttled). Accelerate it: fire NOW instead
  // of waiting for the backoff timer. This avoids a race where both the
  // timer and this function call connect() simultaneously.
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    deps.setStatus("err", "reconnecting…");
    connect();
    return;
  }
  // No socket, or socket in CLOSING/CLOSED state with no pending
  // reconnect → start one now.
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    reconnectFromProbe();
    return;
  }
  // Socket claims to be OPEN, but it may be silently dead after a long
  // backgrounding (Android can keep a killed socket in OPEN state). Send
  // a probe ping and arm a SHORT watchdog (the visible-timeout value, not
  // the 120s hidden tolerance). If no pong arrives, the watchdog
  // force-closes the socket → onclose → scheduleReconnect fires.
  if (pongTimeout) clearTimeout(pongTimeout);
  try {
    ws.send(JSON.stringify({ type: "ping" }));
    // Refocus on a healthy socket — refresh the session list so anything
    // that changed while we were away (or never loaded) shows up.
    ws.send(JSON.stringify({ type: "list_sessions" }));
  } catch {
    // send threw — socket is dead.
    reconnectFromProbe();
    return;
  }
  pongTimeout = setTimeout(() => {
    pongTimeout = null;
    console.warn("[ws] No pong after refocus probe — closing dead socket");
    deps.setStatus("err", "connection stalled");
    try { ws.close(); } catch {}
  }, 7000);
}

function onMessage(raw) {
  let msg; try { msg = JSON.parse(raw.data); } catch { return; }
  switch (msg.type) {
    case "ready":
      deps.setSessionActive(true);
      deps.setResuming(false);
      deps.setSessionId(msg.sessionId);
      deps.getLiveSessionIds().add(msg.sessionId);
      deps.setConfigOptions(msg.configOptions || []); setConfigOptions(deps.getConfigOptions());
      deps.setLoadingHistory(false); // history replay done (or none to replay)
      deps.incrementCacheRestoreToken(); // invalidate any in-flight cache restore
      // Cancel any stale paginated replay from a previous session.
      deps.setReplayingHistory(false);
      hideReplayLoader();
      // If we asked to resume a specific session but got a different one,
      // the resume failed and a new session was created. Clear stale cached
      // messages from the old session so the user doesn't see orphaned history.
      const expectedSessionId = deps.getExpectedSessionId();
      if (expectedSessionId && expectedSessionId !== msg.sessionId) {
        inner.innerHTML = "";
        deps.getAgentMsgs().clear();
        deps.getToolCards().clear();
        clearCachedSession(expectedSessionId);
        deps.setExpectedSessionId(null);
      }
      // Update the active folder from the server's cwd for this session.
      if (msg.cwd) {
        deps.setSelectedDir(msg.cwd);
        deps.setActiveCwd(msg.cwd);
      }
      saveActiveSession(deps.getActiveCwd(), msg.sessionId);
      deps.setStatus("live", "session " + msg.sessionId.slice(0, 12));
      deps.setBusy(false);
      input.disabled = false;
      sendBtn.disabled = false;
      attachBtn.disabled = false;
      input.focus();
      if (msg.availableModes) deps.setAvailableModes(msg.availableModes);
      if (msg.currentModeId !== undefined) deps.setCurrentModeId(msg.currentModeId);
      if (msg.turbo) deps.setTurboEnabled(msg.turbo.enabled);
      deps.updateModeIndicator();
      renderModelPicker();
      // Restore saved model selection if it differs from current
      restoreSavedModel(send);
      deps.renderSidebar();
      updateSessionsBadge();
      // Request the session list so the sidebar populates after a (re)connect.
      // doStartSession clears `sessions` on every connect, so without this the
      // sidebar stays empty until the user manually opens the Sessions modal.
      send({ type: "list_sessions" });
      break;
    case "reattached":
      // Server subscribed us to an existing devin acp process.
      // The agent has full context — no session/load was needed.
      deps.setSessionActive(true);
      deps.setResuming(false);
      deps.setSessionId(msg.sessionId);
      deps.getLiveSessionIds().add(msg.sessionId);
      deps.setLoadingHistory(false); // no history replay on reattach
      deps.incrementCacheRestoreToken(); // invalidate any in-flight cache restore
      // Cancel any stale paginated replay from a previous session.
      deps.setReplayingHistory(false);
      hideReplayLoader();
      // Update the active folder from the server's cwd for this session.
      if (msg.cwd) {
        deps.setSelectedDir(msg.cwd);
        deps.setActiveCwd(msg.cwd);
      }
      saveActiveSession(deps.getActiveCwd(), msg.sessionId);
      deps.setStatus("live", "session " + msg.sessionId.slice(0, 12));
      // Don't clear queueState here — the server now sends an
      // authoritative `queue_update` immediately after `reattached`,
      // so the tray will be rendered from server truth. Clearing
      // locally first would cause a flash of empty tray.
      deps.setBusy(msg.busy); // agent might be mid-workflow
      input.disabled = false;
      sendBtn.disabled = false;
      attachBtn.disabled = false;
      if (msg.availableModes) deps.setAvailableModes(msg.availableModes);
      if (msg.currentModeId !== undefined) deps.setCurrentModeId(msg.currentModeId);
      if (msg.turbo) deps.setTurboEnabled(msg.turbo.enabled);
      deps.updateModeIndicator();
      // Restore config options (model picker, etc.) from the reattached
      // session so the UI reflects the actual state of the running process.
      if (msg.configOptions) {
        deps.setConfigOptions(msg.configOptions); setConfigOptions(deps.getConfigOptions());
        renderModelPicker();
        restoreSavedModel(send);
      }
      if (!msg.busy) input.focus();
      deps.renderSidebar();
      // If agent is busy, show typing indicator
      if (msg.busy) deps.setTyping(true);
      // Repopulate the sidebar after a reconnect (doStartSession cleared it).
      send({ type: "list_sessions" });
      break;
    case "sessions_update":
    case "sessions_list": {
      deps.setSessions((msg.sessions || []).filter((s) => !deps.getDeletedSessionIds().has(s.sessionId)));
      deps.setLiveSessionIds(new Set(msg.liveSessionIds || []));
      // Deep link (?session= from a notification cold start): switch to the
      // target conversation as soon as we see it in the list. One-shot —
      // cleared whether or not it's found so later lists don't re-trigger.
      const pendingDeepLinkSession = deps.getPendingDeepLinkSession();
      if (pendingDeepLinkSession) {
        const target = pendingDeepLinkSession;
        const sessions = deps.getSessions();
        const found = sessions.some((s) => s.sessionId === target);
        deps.setPendingDeepLinkSession(null);
        if (found && target !== deps.getCurrentSessionId()) {
          deps.loadSessionFromHistory(target);
          break; // this list is stale the moment we switch — skip re-render
        }
      }
      // Clean up: remove deleted IDs that are no longer in the server's list
      const incomingIds = new Set((msg.sessions || []).map((s) => s.sessionId));
      const deletedSessionIds = deps.getDeletedSessionIds();
      for (const id of deletedSessionIds) {
        if (!incomingIds.has(id)) deletedSessionIds.delete(id);
      }
      // Update the tab title with the active session's displayName.
      const currentSessionId = deps.getCurrentSessionId();
      if (currentSessionId) {
        const sessions = deps.getSessions();
        const active = sessions.find((s) => s.sessionId === currentSessionId);
        if (active && active.displayName) {
          const cwd = active.cwd || deps.getActiveCwd?.();
          if (cwd) setActiveCwd(cwd, active.displayName);
        }
      }
      deps.recomputeLiveCwds();
      deps.renderSidebar();
      updateSessionsBadge();
      if (sessionsOverlay && !sessionsOverlay.classList.contains("hidden")) renderSessionsModal();
      break;
    }
    case "session_loaded":
      deps.setSessionId(msg.sessionId);
      deps.getLiveSessionIds().add(msg.sessionId);
      deps.setSessionActive(true);
      deps.setResuming(false);
      deps.setLoadingHistory(false); // history replay done
      deps.incrementCacheRestoreToken(); // invalidate any in-flight cache restore
      // Cancel any stale paginated replay from a previous session.
      deps.setReplayingHistory(false);
      hideReplayLoader();
      // Update the active folder from the server's cwd for this session.
      if (msg.cwd) {
        deps.setSelectedDir(msg.cwd);
        deps.setActiveCwd(msg.cwd);
      }
      saveActiveSession(deps.getActiveCwd(), msg.sessionId);
      deps.setBusy(false);
      input.disabled = false;
      sendBtn.disabled = false;
      // Apply modes advertised by session/load (or session/resume).
      if (msg.availableModes) deps.setAvailableModes(msg.availableModes);
      if (msg.currentModeId !== undefined) deps.setCurrentModeId(msg.currentModeId);
      if (msg.turbo) deps.setTurboEnabled(msg.turbo.enabled);
      deps.updateModeIndicator();
      // Restore config options (model picker, etc.) for the loaded session.
      if (msg.configOptions) {
        deps.setConfigOptions(msg.configOptions); setConfigOptions(deps.getConfigOptions());
        renderModelPicker();
        restoreSavedModel(send);
      }
      input.focus();
      deps.renderSidebar();
      // Repopulate the sidebar after loading a session from history.
      send({ type: "list_sessions" });
      break;
    case "session_stopped":
      deps.getLiveSessionIds().delete(msg.sessionId);
      // If we were viewing the stopped session, reflect that in the UI.
      // Clear currentSessionId so clicking the conversation again
      // re-loads it (spawns a new process) instead of early-returning.
      if (msg.sessionId === deps.getCurrentSessionId()) {
        deps.setSessionActive(false);
        deps.setBusy(false);
        deps.setSessionId(null);
        deps.setCurrentModeId(null);
        deps.updateModeIndicator();
        deps.setStatus("err", "session stopped — click to restart");
      }
      deps.renderSidebar();
      updateSessionsBadge();
      if (sessionsOverlay && !sessionsOverlay.classList.contains("hidden")) renderSessionsModal();
      break;
    case "config_options":
      deps.setConfigOptions(msg.configOptions || []); setConfigOptions(deps.getConfigOptions());
      renderModelPicker();
      break;
    case "history_replay":
      // Legacy: server sent the entire history as one frame (DB fallback).
      // Clear the view and render all updates in order (oldest-first append
      // → oldest at top, newest at bottom).
      deps.setLoadingHistory(false);
      // Invalidate any in-flight cache restore so it doesn't inject stale
      // nodes after we've cleared the DOM below.
      deps.incrementCacheRestoreToken();
      deps.setReplayingHistory(false);
      hideReplayLoader();
      deps.resetNodeCacheState();
      inner.innerHTML = "";
      deps.getAgentMsgs().clear();
      deps.getToolCards().clear();
      for (const update of msg.updates) {
        handleUpdate(update);
      }
      deps.scrollDown();
      deps.cacheVisibleNodes();
      break;
    case "history_replay_start":
      // Server is starting a paginated replay.
      deps.setLoadingHistory(false);
      // Invalidate any in-flight cache restore so it doesn't inject stale
      // nodes after we've started.
      deps.incrementCacheRestoreToken();
      // One-shot state restore: usage/mode/commands kinds are filtered out
      // of paged history, so the server sends their latest values here to
      // bring back the context meter, mode badge, and slash commands.
      for (const u of (msg.stateUpdates || [])) handleUpdate(u);
      deps.setReplayingHistory(true);
      deps.setReplayTotal(msg.total);
      deps.setReplayReceived(0);
      deps.setReplayPageSize(msg.pageSize);
      deps.setReplayOldestCursor(null);
      deps.setReplayFirstPageRendered(false);
      deps.resetNodeCacheState();
      // If cached nodes are already on screen, do not wipe the view or flash
      // a loading spinner. Instead, keep the cached view visible and swap
      // atomically when the authoritative first page arrives.
      if (inner.firstElementChild) {
        renderState.replaySwapPending = true;
      } else {
        renderState.replaySwapPending = false;
        inner.innerHTML = "";
        deps.getAgentMsgs().clear();
        deps.getToolCards().clear();
        showReplayLoader();
      }
      break;
    case "history_replay_page":
      // A page of history updates from the server.
      if (msg.page === -1) {
        // Scroll-back fetch (page=-1): prepend older updates above the
        // existing content. Don't clear the view or ACK.
        // Preserve scroll position: measure the height before inserting,
        // then adjust scrollTop after so the user stays at the same spot.
        const scrollHeightBefore = messagesEl.scrollHeight;
        const scrollTopBefore = messagesEl.scrollTop;
        // Set renderState.addElInsertBefore so all addEl calls during handleUpdate
        // insert before the first child (oldest currently shown). The
        // reference is held fixed for the whole batch, so to end up with
        // oldest-at-top we must render oldest-first. The server returns
        // scroll-back pages ASC (oldest-first), so render forward.
        renderState.addElInsertBefore = inner.firstChild;
        renderState.renderingPage = true;
        renderState.pageNodes = [];
        renderState.prependSuffixStart = computePrependSuffixStart(msg.updates);
        for (let i = 0; i < msg.updates.length; i++) {
          handleUpdate(msg.updates[i], i);
        }
        flushPrependBuffers();
        renderState.renderingPage = false;
        renderState.addElInsertBefore = null;
        // Tag the page's nodes with the cursor (for whole-page eviction)
        // and cache them per-node.
        for (const node of renderState.pageNodes) node.dataset.cursor = msg.cursor || "";
        void deps.cachePageNodes(renderState.pageNodes, "prepend");
        renderState.pageNodes = [];
        // Restore scroll position: the new content was added above, so
        // shift scrollTop by the height difference.
        const scrollHeightAfter = messagesEl.scrollHeight;
        messagesEl.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
        // Update the oldest cursor for further scroll-back.
        // If hasMore is false, there's no more older history — clear the
        // cursor so the client stops trying to fetch.
        if (msg.hasMore) {
          if (msg.cursor) deps.setReplayOldestCursor(msg.cursor);
        } else {
          deps.setReplayOldestCursor(null);
        }
        // Reset the scroll-back fetch flag so the user can fetch more.
        deps.setScrollBackFetching(false);
      } else if (deps.getReplayingHistory()) {
        deps.setReplayReceived(deps.getReplayReceived() + msg.updates.length);
        updateReplayLoader();
        const firstPage = !deps.getReplayFirstPageRendered();
        if (firstPage && renderState.replaySwapPending) {
          // Seamless atomic swap: render the first page offscreen into a
          // DocumentFragment, then replace inner's children in one shot.
          // The user never sees a blank screen or loading jitter.
          const frag = document.createDocumentFragment();
          renderState.addElTarget = frag;
          renderState.renderingPage = true;
          renderState.pageNodes = [];
          deps.getAgentMsgs().clear();
          deps.getToolCards().clear();

          const updates = msg.updates.slice().reverse();
          for (const update of updates) {
            handleUpdate(update);
          }
          renderState.renderingPage = false;
          renderState.addElTarget = null;

          for (const node of renderState.pageNodes) node.dataset.cursor = msg.cursor || "";
          void deps.cachePageNodes(renderState.pageNodes, "append");
          renderState.pageNodes = [];

          if (msg.hasMore) {
            if (msg.cursor) deps.setReplayOldestCursor(msg.cursor);
          } else {
            deps.setReplayOldestCursor(null);
          }

          inner.replaceChildren(frag);
          renderState.replaySwapPending = false;
          deps.setReplayFirstPageRendered(true);
          deps.trimDomMessages();
          deps.scrollDown(true);

          send({ type: "history_replay_stop" });
          break;
        }
        // Exclude the replay loader from the node count so it doesn't
        // cause the window to fill one node early.
        const replayLoaderEl = deps.getReplayLoaderEl();
        const childCount = inner.children.length - (replayLoaderEl ? 1 : 0);
        const windowFull = childCount >= deps.getMaxDomMessages();
        if (!windowFull) {
          const scrollHeightBefore = messagesEl.scrollHeight;
          const scrollTopBefore = messagesEl.scrollTop;
          // Prepend older pages above the current top; on the first page
          // (empty view) firstChild is the loader → addEl inserts before
          // it, effectively appending above the loader.
          renderState.addElInsertBefore = inner.firstChild;
          renderState.renderingPage = true;
          renderState.pageNodes = [];
          // renderState.addElInsertBefore is held fixed for the batch, so to end up
          // with oldest-at-top we render oldest-first. The server sends
          // pages DESC (newest-first), so reverse to oldest-first. This
          // applies to both the first page (append) and later pages
          // (prepend before the fixed reference).
          const updates = msg.updates.slice().reverse();
          renderState.prependSuffixStart = computePrependSuffixStart(updates);
          for (let i = 0; i < updates.length; i++) {
            handleUpdate(updates[i], i);
          }
          flushPrependBuffers();
          renderState.renderingPage = false;
          renderState.addElInsertBefore = null;
          // Tag the page's nodes with the cursor (for whole-page eviction)
          // and cache them per-node.
          for (const node of renderState.pageNodes) node.dataset.cursor = msg.cursor || "";
          void deps.cachePageNodes(renderState.pageNodes, firstPage ? "append" : "prepend");
          renderState.pageNodes = [];
          // Track the oldest cursor for scroll-back. If hasMore is false,
          // this is the last (oldest) page — clear the cursor so the client
          // doesn't try to fetch older than the beginning of the session.
          if (msg.hasMore) {
            if (msg.cursor) deps.setReplayOldestCursor(msg.cursor);
          } else {
            deps.setReplayOldestCursor(null);
          }
          // Trim any overshoot beyond the window (evicts oldest pages from
          // the top; updates replayOldestCursor to the new top if it
          // actually evicted anything).
          deps.trimDomMessages();
          if (firstPage) {
            deps.setReplayFirstPageRendered(true);
            // Instant scroll: jump to the bottom immediately so that when
            // older pages are prepended (else branch below), the scrollTop-
            // preserving adjustment keeps the user at the bottom rather
            // than pinning them mid-conversation.
            deps.scrollDown(true);
          } else {
            // Older content was added above; keep the user pinned to the
            // bottom (latest) by shifting scrollTop with the added height.
            const scrollHeightAfter = messagesEl.scrollHeight;
            messagesEl.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
          }
        }
        // Stop the initial replay after the FIRST page (the most recent
        // ~50 messages). The user sees the latest conversation immediately;
        // older history is fetched on demand via scroll-back (history_fetch)
        // when the user scrolls to the top, using replayOldestCursor. This
        // avoids syncing hundreds of older pages that the user hasn't asked
        // for. Also stop if the DOM window filled up (safety net for very
        // large pages).
        const replayLoaderEl2 = deps.getReplayLoaderEl();
        const postCount = inner.children.length - (replayLoaderEl2 ? 1 : 0);
        if (firstPage || postCount >= deps.getMaxDomMessages()) {
          send({ type: "history_replay_stop" });
        } else {
          send({ type: "history_replay_ack", page: msg.page });
        }
      }
      break;
    case "history_replay_end":
      // Paginated replay complete — hide loader, cache the visible window.
      deps.setReplayingHistory(false);
      renderState.replaySwapPending = false;
      renderState.addElTarget = null;
      hideReplayLoader();
      // If the replay produced no message nodes (empty session, or all
      // pages were ACK-and-discard beyond the window), clear any stale
      // cached nodes that restoreCachedNodes may have injected before the
      // replay started. Without this, reopening a session whose history
      // was trimmed/cleared leaves old cached DOM visible.
      deps.setLoadingHistory(false);
      deps.incrementCacheRestoreToken();
      let hasMsgNode = false;
      for (const child of inner.children) {
        if (child.classList.contains("msg-row") || child.classList.contains("tool-card")) {
          hasMsgNode = true;
          break;
        }
      }
      if (!hasMsgNode) {
        inner.innerHTML = "";
        deps.getAgentMsgs().clear();
        deps.getToolCards().clear();
        const currentSessionId = deps.getCurrentSessionId();
        if (currentSessionId) void clearCachedSession(currentSessionId);
      }
      // Force an instant jump to the bottom — this is the user's landing
      // position after reopening/switching a session. Using instant (not
      // smooth) scroll guarantees we land at the bottom regardless of any
      // userPinnedAway state that may have been tripped during the replay.
      deps.scrollDown(true);
      deps.cacheVisibleNodes();
      break;
    case "update": {
      handleUpdate(msg.update);
      // Detect completed tool calls that require user attention (Sentry,
      // Compass). Fire an in-tab notification (Layer 2) so a backgrounded
      // tab gets a toast + title badge. The server's Web Push (Layer 3)
      // covers the closed-tab case independently.
      const u = msg.update;
      if (
        u && !deps.getLoadingHistory() && !deps.getReplayingHistory() &&
        (u.sessionUpdate === "tool_call" || u.sessionUpdate === "tool_call_update") &&
        u.status === "completed" &&
        isAttentionTool(u.name, u.title)
      ) {
        bumpUnread();
        const toolLabel = (u.name || "Tool").charAt(0).toUpperCase() + (u.name || "Tool").slice(1).toLowerCase();
        maybeNotify("tool_attention", notifTitle("Devin — " + toolLabel + " result ready"), u.title || "A tool result is ready for review.");
      }
      break;
    }
    case "turn_end":
      // The user missed the finish (tab hidden / app backgrounded) —
      // badge the tab and fire an OS notification if enabled. (The server
      // also pushes this event to every device; matching tags merge them.)
      // Turbo-continuing turn_ends are intermediate — the chain is still
      // running — so they're deliberately silent.
      if (!msg.turbo?.continuing) {
        bumpUnread();
        maybeNotify("turn_end", notifTitle("Devin finished"), "The agent finished working.");
      }
      // queueState is now driven by `queue_update` messages from the
      // server, not by reconciling against queueDepth here. The server
      // always sends a queue_update immediately after turn_end (from
      // markDone + deliverQueueUpdate in the main loop), so we let that
      // drive setBusy — calling it here would race against a stale
      // queueState.running that hasn't been cleared yet.
      deps.setQueuedPromptCount(msg.queueDepth ?? 0);
      deps.renderQueuedTray();
      deps.getAgentMsgs().clear();
      deps.getToolCards().clear();
      // Final cache write (immediate, not debounced) — ensure latest state is persisted
      const cacheTimer = deps.getCacheTimer();
      if (cacheTimer) { clearTimeout(cacheTimer); deps.setCacheTimer(null); }
      deps.cacheVisibleNodes();
      // Refresh session list since a new session may now have history
      // (e.g. a title was generated, or the updatedAt timestamp changed).
      send({ type: "list_sessions" });
      break;
    case "mode_changed":
      deps.setCurrentModeId(msg.modeId);
      deps.updateModeIndicator();
      break;
    case "turbo_state":
      // Server-authoritative turbo toggle (mode menu, /turbo, or a
      // persisted preference applied at session start).
      deps.setTurboEnabled(msg.enabled);
      deps.updateModeIndicator();
      sysMsg(msg.enabled
        ? `⚡ turbo on — bypass permissions, auto-continuing (up to ${msg.maxIterations ?? "?"} checks)`
        : "⚡ turbo off");
      break;
    case "permission_request":
      // Urgent: the agent is blocked until someone answers. Badge + notify
      // (server also pushes to every device; tags merge).
      bumpUnread();
      maybeNotify("permission_needed", notifTitle("Devin needs approval"), msg.toolCall?.title || "A tool permission is waiting.");
      renderPermission(msg);
      break;
    case "permission_resolved": dismissPermissionCard(msg.id, msg.outcome); break;
    case "elicitation_request":
      // Urgent like a permission request: the agent's elicitation/create
      // call is parked until someone answers the card.
      bumpUnread();
      maybeNotify("permission_needed", notifTitle("Devin needs input"), msg.message || "The agent is asking a question.");
      renderElicitation(msg);
      break;
    case "elicitation_resolved": dismissElicitationCard(msg.id, msg.action); break;
    case "elicitation_completed": completeElicitationCard(msg.elicitationId); break;
    case "error":
      sysMsg(msg.message, true);
      deps.setStatus("err", "error");
      bumpUnread();
      maybeNotify("error", notifTitle("Devin hit an error"), (msg.message || "").slice(0, 120));
      // If the server still has queued prompts, stay busy — the next
      // turn will start automatically after this error. For turn errors
      // a queue_update follows and will correct the state if needed; for
      // fatal errors (no session) no queue_update arrives, so this
      // setBusy(false) is the fallback that hides the typing indicator.
      deps.setQueuedPromptCount(msg.queueDepth ?? 0);
      deps.renderQueuedTray();
      deps.setBusy(deps.getQueuedPromptCount() > 0 || deps.getQueueState().queued.length > 0);
      // If the session loaded successfully (just a turn error), stay in chat view.
      // If no session ever started, show onboarding — but not if we were
      // resuming (resume errors show in the chat view with a retry option).
      if (!deps.getSessionActive() && !deps.getResuming()) {
        onboardStart.disabled = false;
        onboardStart.textContent = "Start session";
        setTimeout(() => deps.showOnboarding(), 1500);
      }
      deps.setResuming(false);
      break;
    case "closed":
      const currentSessionId = deps.getCurrentSessionId();
      if (currentSessionId) deps.getLiveSessionIds().delete(currentSessionId);
      deps.setStatus("", "closed");
      deps.setBusy(false);
      deps.setSessionActive(false);
      deps.setSessionId(null);
      deps.setCurrentModeId(null);
      deps.updateModeIndicator();
      deps.renderSidebar();
      break;
    // ---- Shell messages ----
    case "shell_created":
      onShellCreated(msg);
      break;
    case "shell_output":
      onShellOutput(msg);
      break;
    case "shell_exit":
      onShellExit(msg);
      break;
    case "shell_list":
      onShellList(msg);
      break;
    case "shell_error":
      console.error("[shell]", msg.message);
      break;
    case "pong":
      // Heartbeat reply — liveness is already tracked in ws.onmessage
      // (any message clears the pong watchdog). Nothing else to do here.
      break;
    case "queue_update":
      // Authoritative prompt-queue snapshot from the server. This is
      // the ONLY place queueState is mutated (aside from local resets
      // on new-session / folder-change). Re-render the tray from it.
      deps.setQueueState({
        running: msg.running ?? null,
        queued: Array.isArray(msg.queued) ? msg.queued : [],
      });
      deps.setQueuedPromptCount(deps.getQueueState().queued.length);
      deps.renderQueuedTray();
      // The queue snapshot is the authoritative source of busy state.
      // The server always sends a queue_update after every turn_end /
      // markRunning / markDone, so this is what drives the typing
      // indicator on/off — not turn_end (whose queueState.running is
      // stale at arrival time, since the clearing queue_update hasn't
      // arrived yet).
      deps.setBusy(deps.getQueueState().running != null || deps.getQueueState().queued.length > 0);
      break;
  }
}
